import { getAnalyticsErrorType } from '@zg-rentals/rentals-analytics';
import { logError } from '@zg-rentals/log-error';
import { RenterProfileHasPets } from '@zg-rentals/ts-your-rental-api-client';
import {
  RenterProfileHasPets as RenterInfoHasPets,
  type RenterInfoRenterProfileStandalonePageWebApiResponse,
  RenterProfileStandalonePageCreditScore,
  RenterProfileStandalonePagePreferredBedroomCount,
  RenterProfileStandalonePageTotalPeopleInHousehold,
} from '@zg-rentals/ts-renter-info-client';
import type { RenterInfoClickstreamUser, RenterProfile } from './types';

const nullValues = {
  renter_profile: {
    credit_score_txt: null,
    parking_needed_ind: null,
    pets_ind: null,
    income_txt: null,
    lease_length_txt: null,
    move_in_dt: null,
    bed_cnt_txt: null,
    occupants_cnt_txt: null,
  },
};

export function getRenterProfileInfoForYourRental(user?: RenterInfoClickstreamUser | null): RenterProfile {
  try {
    if (!user) {
      return nullValues;
    }
    const {
      creditScoreMax,
      creditScoreMin,
      desiredParking,
      hasPets,
      incomeYearly,
      leaseLengthMonths,
      moveInDate,
      numBedrooms,
      numOccupants,
    } = user;
    return {
      renter_profile: {
        credit_score_txt: creditScoreMax && creditScoreMin ? `${creditScoreMin}-${creditScoreMax}` : null,
        parking_needed_ind: desiredParking === 'true',
        pets_ind: hasPets === RenterProfileHasPets.YES,
        income_txt: incomeYearly ? incomeYearly.toString() : null,
        lease_length_txt: leaseLengthMonths ? leaseLengthMonths.toString() : null,
        move_in_dt: moveInDate ? `${moveInDate.year}-${moveInDate.month}-${moveInDate.day}` : null,
        bed_cnt_txt: numBedrooms ? numBedrooms.toString() : null,
        occupants_cnt_txt: numOccupants ? numOccupants.toString() : null,
      },
    };
  } catch (error) {
    logError({
      error,
      errorType: getAnalyticsErrorType('getRenterProfileInfoForYourRental'),
      context: { argumentPassed: user },
    });
    return nullValues;
  }
}

const CREDIT_SCORE_TXT: Record<RenterProfileStandalonePageCreditScore, string> = {
  [RenterProfileStandalonePageCreditScore.RANGE_300_579]: '300-579',
  [RenterProfileStandalonePageCreditScore.RANGE_580_619]: '580-619',
  [RenterProfileStandalonePageCreditScore.RANGE_620_659]: '620-659',
  [RenterProfileStandalonePageCreditScore.RANGE_660_719]: '660-719',
  [RenterProfileStandalonePageCreditScore.RANGE_720_PLUS]: '720+',
  [RenterProfileStandalonePageCreditScore.UNKNOWN]: 'Unknown',
};

const BEDROOM_COUNT_TXT: Record<RenterProfileStandalonePagePreferredBedroomCount, string> = {
  [RenterProfileStandalonePagePreferredBedroomCount.STUDIO]: 'STUDIO',
  [RenterProfileStandalonePagePreferredBedroomCount.ONE_BD]: '1',
  [RenterProfileStandalonePagePreferredBedroomCount.TWO_BDS]: '2',
  [RenterProfileStandalonePagePreferredBedroomCount.THREE_BDS]: '3',
  [RenterProfileStandalonePagePreferredBedroomCount.FOUR_PLUS_BDS]: '4+',
};

const OCCUPANTS_CNT_TXT: Record<RenterProfileStandalonePageTotalPeopleInHousehold, string> = {
  [RenterProfileStandalonePageTotalPeopleInHousehold.ONE]: '1',
  [RenterProfileStandalonePageTotalPeopleInHousehold.TWO]: '2',
  [RenterProfileStandalonePageTotalPeopleInHousehold.THREE]: '3',
  [RenterProfileStandalonePageTotalPeopleInHousehold.FOUR]: '4',
  [RenterProfileStandalonePageTotalPeopleInHousehold.FIVE_PLUS]: '5+',
};

const petsToIndicator = (pets: RenterInfoRenterProfileStandalonePageWebApiResponse['pets']): boolean | null => {
  if (pets === RenterInfoHasPets.YES) return true;
  if (pets === RenterInfoHasPets.NO) return false;
  return null;
};

export function getRenterProfileInfoForRenterProfilePage(
  profile?: RenterInfoRenterProfileStandalonePageWebApiResponse | null,
): RenterProfile {
  try {
    if (!profile) {
      return nullValues;
    }
    return {
      renter_profile: {
        credit_score_txt: profile.creditScore ? CREDIT_SCORE_TXT[profile.creditScore] : null,
        parking_needed_ind: null,
        pets_ind: petsToIndicator(profile.pets),
        income_txt: profile.estimatedHouseholdIncome != null ? String(profile.estimatedHouseholdIncome) : null,
        lease_length_txt: profile.preferredLeaseDuration ?? null,
        move_in_dt: profile.preferredMoveInDate
          ? `${profile.preferredMoveInDate.year}-${profile.preferredMoveInDate.month}-${profile.preferredMoveInDate.day}`
          : null,
        bed_cnt_txt: profile.preferredBedroomCount ? BEDROOM_COUNT_TXT[profile.preferredBedroomCount] : null,
        occupants_cnt_txt: profile.totalPeopleInHousehold ? OCCUPANTS_CNT_TXT[profile.totalPeopleInHousehold] : null,
      },
    };
  } catch (error) {
    logError({
      error,
      errorType: getAnalyticsErrorType('getRenterProfileInfoForRenterProfilePage'),
      context: { argumentPassed: profile },
    });
    return nullValues;
  }
}
